import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { ClientStorageTypes } from "src/constants";
import { listBulkActions } from "src/generated/hooks/form-service/requests";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import useUser from "src/hooks/useUser";
import { localStorageRecord } from "src/services/localStorage";
import { Status } from "src/types/authData";
import { z } from "zod";

const LATEST_ACTIVITY_QUERY_KEY = "bulkActionsLatest";

const lastViewedStore = localStorageRecord(
  ClientStorageTypes.FormActivityLastViewed,
  z.record(z.string().min(1), z.string())
);

const storageKey = (userId: string, formTemplateId: string) =>
  `${userId}:${formTemplateId}`;

const hasUnreadActivities = (
  latestErroredCompletedAt: string | null,
  lastViewedAt: string | null
): boolean => {
  if (!latestErroredCompletedAt) {
    return false;
  }

  const latestMs = Date.parse(latestErroredCompletedAt);
  if (Number.isNaN(latestMs)) {
    return false;
  }

  if (!lastViewedAt) {
    return true;
  }

  const viewedMs = Date.parse(lastViewedAt);
  if (Number.isNaN(viewedMs)) {
    return true;
  }

  return latestMs > viewedMs;
};

const getLastViewedActivityAt = (
  userId: string,
  formTemplateId: string
): string | null =>
  lastViewedStore.getValue()?.[storageKey(userId, formTemplateId)] ?? null;

const setLastViewedActivityAt = (
  userId: string,
  formTemplateId: string,
  viewedAt: string = new Date().toISOString()
): void => {
  lastViewedStore.setValue({
    ...(lastViewedStore.getValue() ?? {}),
    [storageKey(userId, formTemplateId)]: viewedAt,
  });
};

const useCurrentUserId = (): string | undefined => {
  const user = useUser();
  return user.status === Status.OK ? user.data.id : undefined;
};

export const useUnreadFormActivities = (
  formTemplateId: string | undefined
): boolean => {
  const userId = useCurrentUserId();

  const latestRD = useRemoteDataRestQuery({
    queryKey: [LATEST_ACTIVITY_QUERY_KEY, formTemplateId, userId],
    queryFn: () =>
      listBulkActions({
        query: {
          formTemplateId: formTemplateId ?? "",
          limit: 1,
          offset: 0,
          status: "DONE_WITH_ERRORS",
          sortKey: "done",
          sortType: "desc",
        },
      }).then((response) => response.data?.data?.bulkActions[0] ?? null),
    enabled: !!formTemplateId && !!userId,
  });

  const latestErroredCompletedAt = latestRD.toNullable()?.completedAt ?? null;
  const lastViewedAt =
    userId && formTemplateId
      ? getLastViewedActivityAt(userId, formTemplateId)
      : null;

  return hasUnreadActivities(latestErroredCompletedAt, lastViewedAt);
};

export const useRefreshUnreadFormActivities = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (formTemplateId: string) =>
      queryClient.invalidateQueries({
        queryKey: [LATEST_ACTIVITY_QUERY_KEY, formTemplateId],
      }),
    [queryClient]
  );
};

export const useMarkFormActivitiesViewed = (
  formTemplateId: string | undefined
): void => {
  const userId = useCurrentUserId();

  useEffect(() => {
    if (!userId || !formTemplateId) {
      return;
    }
    setLastViewedActivityAt(userId, formTemplateId);
  }, [userId, formTemplateId]);
};
